'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register plugin once
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export function useGsapAnimations() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Performance config - optimized for smooth animations
    gsap.config({
      force3D: true,
      nullTargetWarn: false,
    });

    // Reduce scroll trigger refresh rate for better performance
    ScrollTrigger.config({
      limitCallbacks: true,
      ignoreMobileResize: true,
    });

    // Set all animated elements to their initial state immediately to prevent flash
    const allAnimatedElements = containerRef.current.querySelectorAll('.animate-on-scroll, .animate-stagger > *');
    gsap.set(allAnimatedElements, { opacity: 0, y: 30 });

    const ctx = gsap.context(() => {
      // Hero content - immediate animation on load
      const heroContent = containerRef.current?.querySelector('.animate-hero');
      if (heroContent) {
        gsap.fromTo(
          heroContent.children,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out',
          }
        );
      }

      // Hero stats
      const heroStats = containerRef.current?.querySelector('.animate-hero-stats');
      if (heroStats) {
        gsap.fromTo(
          heroStats.children,
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.1,
            delay: 0.5,
            ease: 'power2.out',
          }
        );
      }

      // Scroll elements - use individual triggers for smoother performance
      const scrollElements = containerRef.current?.querySelectorAll('.animate-on-scroll');
      scrollElements?.forEach((element) => {
        gsap.to(element, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 85%',
            once: true,
          },
        });
      });

      // Stagger containers
      const staggerContainers = containerRef.current?.querySelectorAll('.animate-stagger');
      staggerContainers?.forEach((container) => {
        const children = container.children;
        if (children.length === 0) return;

        ScrollTrigger.create({
          trigger: container,
          start: 'top 85%',
          once: true,
          onEnter: () => {
            gsap.to(children, {
              y: 0,
              opacity: 1,
              duration: 0.5,
              stagger: 0.08,
              ease: 'power2.out',
            });
          },
        });
      });

    }, containerRef);

    return () => {
      ctx.revert();
    };
  }, []);

  return containerRef;
}
